import * as React from "react";
import { useMemo, useState, useCallback } from "react";
import SelectEntitiesContext from "components/common/EntityDataTable/contexts/SelectEntitiesContext";
import { createSelectedEntityId } from "views/hooks/useSelectedMessageEntities";
const removeSelectedEntityId = (selectedEntities, targetEntityId) => selectedEntities.filter((entityId) => entityId !== targetEntityId);
const isUpdateFunction = (setSelectedEntitiesArgument) => typeof setSelectedEntitiesArgument === "function";
const SelectedMessageEntitiesProvider = ({
  children = void 0,
  initialSelection = [],
  onChangeSelection = void 0,
  entities
}) => {
  const [selectedEntities, setSelectedEntities] = useState(initialSelection);
  const _setSelectedEntities = useCallback(
    (setSelectedEntitiesArgument) => {
      setSelectedEntities((currentSelectedEntities) => {
        const newState = isUpdateFunction(setSelectedEntitiesArgument) ? setSelectedEntitiesArgument(currentSelectedEntities) : setSelectedEntitiesArgument;
        if (onChangeSelection) {
          onChangeSelection(newState, entities);
        }
        return newState;
      });
    },
    [entities, onChangeSelection]
  );
  const deselectEntity = useCallback(
    (targetEntityId) => _setSelectedEntities((cur) => removeSelectedEntityId(cur, targetEntityId)),
    [_setSelectedEntities]
  );
  const selectEntity = useCallback(
    (targetEntityId) => _setSelectedEntities((cur) => [...cur, targetEntityId]),
    [_setSelectedEntities]
  );
  const toggleEntitySelect = useCallback(
    (targetEntityId) => {
      _setSelectedEntities((cur) => {
        if (cur.includes(targetEntityId)) {
          return removeSelectedEntityId(cur, targetEntityId);
        }
        return [...cur, targetEntityId];
      });
    },
    [_setSelectedEntities]
  );
  const visibleEntityIds = useMemo(
    () => entities.map(({ id, index }) => createSelectedEntityId(index, id)),
    [entities]
  );
  const selectedVisibleEntitiesCount = useMemo(
    () => visibleEntityIds.filter((entityId) => selectedEntities.includes(entityId)).length,
    [selectedEntities, visibleEntityIds]
  );
  const isAllRowsSelected = visibleEntityIds.length > 0 && selectedVisibleEntitiesCount === visibleEntityIds.length;
  const isSomeRowsSelected = selectedVisibleEntitiesCount > 0 && !isAllRowsSelected;
  const contextValue = useMemo(
    () => ({
      setSelectedEntities: _setSelectedEntities,
      selectedEntities,
      deselectEntity,
      selectEntity,
      toggleEntitySelect,
      isAllRowsSelected,
      isSomeRowsSelected
    }),
    [
      _setSelectedEntities,
      selectedEntities,
      deselectEntity,
      selectEntity,
      toggleEntitySelect,
      isAllRowsSelected,
      isSomeRowsSelected
    ]
  );
  return /* @__PURE__ */ React.createElement(SelectEntitiesContext.Provider, { value: contextValue }, children);
};
export default SelectedMessageEntitiesProvider;
